import _, { sortBy, uniqBy } from 'lodash';
import React, { createContext, PureComponent, useContext } from 'react';
import { ISource } from '@stores/source';
import { IDestination } from '@stores/destination';
import { FixMe } from '@utils/types';
import { IColumn } from '@components/sources/source/warehouseSource/configureData/sourceSpecificConfig';
import { ErrorProps } from './errorsView';

export interface ConnectionProps {
  source: ISource;
  modelAccountId?: string;
  destination: Partial<IDestination>;
  sourceConfig?: ISourceConfig;
  destinationConfig?: IDestinationConfig;
  mappingConfig?: IMappingConfig[];
  uniqueIdentifierConfig?: IMappingConfig;
  onChange?: (config: Partial<ConnectionState>) => void;
  ifUpdate?: boolean;
  fields?: IField[];
  columns?: IColumn[];
  viewOnly?: boolean;
  enteredSaveState?: boolean;
  setBottomEditButtonVisible?: (show: boolean) => void;
  updateSchemaErrors: (errorItems: ErrorProps[]) => void;
  syncBehaviour?: {
    mode: string;
  };
}
export interface ISourceConfig {
  table?: {
    name?: string;
    schema?: string;
    bucketName?: string;
    prefix?: string;
  };
  sqlModelId?: string;
}
export interface IObject {
  name: string;
  label: string;
  isDefault?: boolean;
  supportsCustomFields?: boolean;
}

export interface IField {
  name: string;
  label: string;
  isDefault?: boolean;
  isUnique?: boolean;
  requiredForMapping?: boolean;
  type?: string;
  createdNow?: boolean;
}
interface IDestinationConfig {
  object?: IObject;
}
export interface IMappingConfig {
  original?: string;
  mapped?: string;
}

interface State {
  modelAccountId?: string;
  sourceConfig: ISourceConfig;
  destinationConfig: IDestinationConfig;
  mappingConfig: IMappingConfig[];
  fields: IField[];
  columns: IColumn[];
  preview: { [column: string]: string };
  uniqueIdentifierConfig: IMappingConfig;
  fieldMap: { [name: string]: IField };
  columnMap: { [name: string]: IColumn };
  ifUpdate?: boolean;
  enteredSaveState?: boolean;
  viewOnly?: boolean;
  syncBehaviour?: {
    mode: string;
  };
}
export interface ConnectionState extends State {
  source: Partial<ISource>;
  destination: Partial<IDestination>;
  setBottomEditButtonVisible?: (show: boolean) => void;
  setSourceConfig: (sourceConfig: ISourceConfig, refresh?: boolean, accountId?: string) => void;
  setDestinationConfig: (destinationConfig: IDestinationConfig) => void;
  setMappingConfig: (
    mappingConfig: IMappingConfig[],
    cl?: () => void,
    setDefault?: boolean,
  ) => void;
  setFields: (fields: IField[], defaultMapping?: boolean) => void;
  setColumns: (columns: IColumn[]) => void;
  setPreview: (preview: { [column: string]: string }) => void;
  setUniqueIdentifierConfig: (
    uniqueIdentifierConfig: IMappingConfig,
    empty?: boolean,
    defaultMapping?: boolean,
  ) => void;
  getDefaultMappingConfig: () => IMappingConfig[];
  createField: (field: IField) => void;
  updateSchemaErrors: (errorItems: ErrorProps[]) => void;
  setSyncBehaviour: (mode: string) => void;
}
const ConnectionContext = createContext<ConnectionState>({
  sourceConfig: {},
  destinationConfig: {},
  mappingConfig: [],
  fields: [],
  columns: [],
  preview: {},
  uniqueIdentifierConfig: {},
  fieldMap: {},
  columnMap: {},
  ifUpdate: false,
  viewOnly: false,
  enteredSaveState: false,
  source: {},
  destination: {},
  syncBehaviour: { mode: '' },
  setFields: () => null,
  setColumns: () => null,
  setSourceConfig: () => null,
  setDestinationConfig: () => null,
  setMappingConfig: () => null,
  setPreview: () => null,
  setUniqueIdentifierConfig: () => null,
  getDefaultMappingConfig: () => [],
  createField: () => null,
  setBottomEditButtonVisible: () => null,
  updateSchemaErrors: () => null,
  setSyncBehaviour: () => null,
});

export const useConnectionState = () => useContext(ConnectionContext);
export const isArrayEqual = (prevObject: FixMe, newObject: FixMe) =>
  _(prevObject).xorWith(newObject, _.isEqual).isEmpty();
class ConnectionProvider extends PureComponent<ConnectionProps, State> {
  constructor(props: ConnectionProps) {
    super(props);
    this.state = {
      sourceConfig: this.props.sourceConfig || {},
      destinationConfig: this.props.destinationConfig || {},
      mappingConfig: this.props.mappingConfig || [],
      fields: sortBy(this.props.fields || [], 'label'),
      columns: this.props.columns || [],
      uniqueIdentifierConfig: this.props.uniqueIdentifierConfig || {},
      columnMap:
        this.props?.columns?.reduce((accum, column) => ({ ...accum, [column.name]: column }), {}) ||
        {},
      fieldMap:
        this.props?.fields?.reduce((accum, field) => ({ ...accum, [field.name]: field }), {}) || {},
      preview: {},
      ifUpdate: !!this.props.ifUpdate,
      viewOnly: !!this.props.viewOnly,
      enteredSaveState: false,
      syncBehaviour: this.props.syncBehaviour,
      modelAccountId: this.props.modelAccountId,
    };
  }

  // eslint-disable-next-line react/no-deprecated
  componentWillReceiveProps(nextProps: ConnectionProps) {
    if (nextProps.enteredSaveState !== this.state.enteredSaveState) {
      this.setState({ enteredSaveState: nextProps.enteredSaveState });
    }
    const newColumnsFromProps = !isArrayEqual(nextProps.columns, this.state.columns);

    if (newColumnsFromProps) {
      this.setColumns(nextProps.columns || []);
    }
  }

  componentDidUpdate(prevProps: ConnectionProps, prevState: ConnectionState) {
    const { uniqueIdentifierConfig, columns, sourceConfig, destinationConfig, fields } = this.state;

    const newColumns = !isArrayEqual(prevState.columns, columns);
    const prevStateSourceConfig: ISourceConfig = prevState.sourceConfig;
    const newStateSourceConfig: ISourceConfig = sourceConfig;
    const newSourceConfig = !isArrayEqual([prevStateSourceConfig], [newStateSourceConfig]);
    const newdestinationConfig = !_.isEqual(prevState.destinationConfig, destinationConfig);

    const newFields = !isArrayEqual(prevState.fields, fields);

    if (newSourceConfig || newdestinationConfig) {
      this.setUniqueIdentifierConfig({}, true, true);
    } else if (newFields) {
      if (
        uniqueIdentifierConfig?.mapped &&
        !fields.some((field) => field.name === uniqueIdentifierConfig?.mapped)
      ) {
        this.setUniqueIdentifierConfig({ mapped: undefined });
      }
    } else if (
      newColumns &&
      uniqueIdentifierConfig?.original &&
      !columns.some((col) => col.name === uniqueIdentifierConfig?.original)
    ) {
      this.setUniqueIdentifierConfig({ original: undefined });
    }
  }

  setSourceConfig = (config: ISourceConfig, refresh?: boolean, accountId?: string) => {
    if (refresh) {
      this.setState({ sourceConfig: {} }, () => {
        this.onChange({ sourceConfig: {} });
      });
      return;
    }
    const oldTableConfig = this.state.sourceConfig?.table;
    const newConfig = config.sqlModelId
      ? { sqlModelId: config.sqlModelId }
      : {
          table: {
            ...(oldTableConfig || {}),
            ...(config.table || {}),
          },
        };

    this.setState({ sourceConfig: newConfig, modelAccountId: accountId });
    this.onChange({ sourceConfig: newConfig });
  };

  setDestinationConfig = (config: IDestinationConfig) => {
    const oldDestConf = this.state.destinationConfig;
    const newConfig = { ...oldDestConf, ...config };
    this.setState(
      {
        destinationConfig: newConfig,
      },
      () => {
        this.onChange({ destinationConfig: newConfig });
      },
    );
  };

  setUniqueIdentifierConfig = (conf: IMappingConfig, empty?: boolean, defaultMapping?: boolean) => {
    if (empty) {
      const mappingConfig = this.getDefaultMappingConfig();
      this.setState(
        {
          mappingConfig,
          uniqueIdentifierConfig: {},
        },
        () => {
          this.onChange({ mappingConfig, uniqueIdentifierConfig: {} });
        },
      );

      return;
    }
    const { uniqueIdentifierConfig, mappingConfig, fieldMap } = this.state;
    const mapped = Object.prototype.hasOwnProperty.call(conf, 'mapped')
      ? conf.mapped
      : uniqueIdentifierConfig?.mapped;
    const original = Object.prototype.hasOwnProperty.call(conf, 'original')
      ? conf.original
      : uniqueIdentifierConfig?.original;
    const newConf = {
      mapped,
      original,
    };
    if (defaultMapping) {
      const defaultMappingConfig = this.getDefaultMappingConfig();
      this.setState(
        {
          mappingConfig: defaultMappingConfig,
          uniqueIdentifierConfig: newConf,
        },
        () => {
          this.onChange({ mappingConfig: defaultMappingConfig, uniqueIdentifierConfig: newConf });
        },
      );
      return;
    }
    let newMappingConfig: IMappingConfig[] = mappingConfig;
    if (uniqueIdentifierConfig?.mapped !== newConf.mapped) {
      newMappingConfig = newMappingConfig?.filter(
        (curr: IMappingConfig) =>
          curr?.mapped !== uniqueIdentifierConfig?.mapped && curr?.mapped !== newConf?.mapped,
      );
    }
    if (uniqueIdentifierConfig?.mapped && uniqueIdentifierConfig?.mapped !== newConf.mapped) {
      const addRequiredAgain = fieldMap[uniqueIdentifierConfig?.mapped]?.requiredForMapping
        ? { mapped: uniqueIdentifierConfig?.mapped }
        : undefined;
      if (addRequiredAgain) {
        newMappingConfig.push(addRequiredAgain);
      }
    }
    if (original && mapped) {
      newMappingConfig = uniqBy(newMappingConfig, 'mapped');
    }

    this.setState(
      {
        mappingConfig: newMappingConfig,
        uniqueIdentifierConfig: newConf,
      },
      () => {
        this.onChange({ uniqueIdentifierConfig: newConf, mappingConfig: newMappingConfig });
      },
    );
  };

  setMappingConfig = (mappingConfig: IMappingConfig[], cl?: () => void, setDefault?: boolean) => {
    const nonEmpty = uniqBy(
      mappingConfig.filter((map: IMappingConfig) => map.original),
      'mapped',
    );
    const defaultMappingConfig = setDefault ? this.getDefaultMappingConfig() : undefined;
    this.setState(
      {
        mappingConfig:
          defaultMappingConfig ||
          mappingConfig.filter(
            (map: IMappingConfig) =>
              nonEmpty.some(
                (m: IMappingConfig) => map.mapped === m.mapped && map.original === m.original,
              ) || !map.original,
          ),
      },
      () => {
        this.onChange({ mappingConfig: this.state.mappingConfig });
        if (cl) {
          cl();
        }
      },
    );
  };

  onChange = (config: Partial<ConnectionState>) => {
    if (this.props.onChange) {
      this.props.onChange(config);
    }
  };

  setColumns = (columns: IColumn[]) => {
    const sortedColumns = sortBy(columns, 'name');
    const columnMap = columns.reduce((accum, x) => ({ ...accum, [x.name]: x }), {});

    this.setState(
      {
        columns: sortedColumns,
        columnMap,
        enteredSaveState: false,
      },
      () => {
        this.onChange({ columns: sortedColumns, columnMap, enteredSaveState: false });
      },
    );
  };

  setPreview = (preview: { [column: string]: string }) => {
    this.setState({
      preview,
    });
  };

  getDefaultMappingConfig = (allFields?: IField[]) => {
    const fields = allFields || this.state.fields;

    const defaultMappingConfig = fields.reduce((accum: IMappingConfig[], field: IField) => {
      if (field.requiredForMapping && field.name !== this.state.uniqueIdentifierConfig?.mapped) {
        accum.push({ mapped: field.name });
      }
      return accum;
    }, []);
    return sortBy(defaultMappingConfig, 'mapped');
  };

  createField = (field: IField) => {
    const { fields, fieldMap } = this.state;
    const ifExists = fields.some((curr) => curr.name === field.name);
    if (ifExists) {
      return;
    }
    const newFieldMap = fieldMap;
    newFieldMap[field.name] = field;
    const newFields = [...fields, field];
    this.setState(
      {
        fields: newFields,
        fieldMap: newFieldMap,
      },
      () => {
        this.onChange({ fields: newFields });
      },
    );
  };

  setFields = (fields: IField[], defaultMapping?: boolean) => {
    const fieldMap = fields.reduce((accum, x) => ({ ...accum, [x.name]: x }), {});
    let { mappingConfig } = this.state;
    if (defaultMapping) {
      mappingConfig = this.getDefaultMappingConfig(fields);
    } else {
      mappingConfig = uniqBy([...mappingConfig, ...this.getDefaultMappingConfig(fields)], 'mapped');
    }

    const newFields = sortBy(fields, 'label');

    this.setState(
      {
        mappingConfig,
        fields: newFields,
        fieldMap,
      },
      () => {
        this.onChange({ mappingConfig, fields: newFields });
      },
    );
  };

  setSyncBehaviour = (mode: string) => {
    const syncBehaviour = { mode };
    this.setState({ syncBehaviour }, () => {
      this.onChange({ syncBehaviour });
    });
  };

  render() {
    const { children, source, setBottomEditButtonVisible, destination, updateSchemaErrors } =
      this.props;

    const values = {
      ...this.state,
      source,
      setBottomEditButtonVisible,
      destination,
      setDestinationConfig: this.setDestinationConfig,
      setSourceConfig: this.setSourceConfig,
      setMappingConfig: this.setMappingConfig,
      setFields: this.setFields,
      setColumns: this.setColumns,
      setPreview: this.setPreview,
      setUniqueIdentifierConfig: this.setUniqueIdentifierConfig,
      getDefaultMappingConfig: this.getDefaultMappingConfig,
      createField: this.createField,
      updateSchemaErrors,
      setSyncBehaviour: this.setSyncBehaviour,
    };

    return <ConnectionContext.Provider value={values}>{children}</ConnectionContext.Provider>;
  }
}

export default ConnectionProvider;
